import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { SmolitMark } from './SmolitMark';

export type BubbleAuthor = 'me' | 'them' | 'smolit';

export interface MessageBubbleProps {
  author: BubbleAuthor;
  children: ReactNode;
  timestamp?: string;
  /**
   * Rendered under the text inside the same bubble — how Smolit returns ride
   * cards, booking confirmations and drafted copy in-thread.
   */
  attachment?: ReactNode;
  className?: string;
}

/**
 * Conversation bubble, shared by member messaging and the Smolit thread. The
 * speaker's own messages are ngahere on the right; everything received is a
 * light bubble on the left with the corner notched towards the sender.
 */
export function MessageBubble({
  author,
  children,
  timestamp,
  attachment,
  className,
}: MessageBubbleProps) {
  const mine = author === 'me';

  return (
    <div className={cn('flex items-start', mine ? 'justify-end' : 'justify-start', className)}>
      {author === 'smolit' ? (
        <span className="mr-2 flex-none">
          <SmolitMark size={28} />
        </span>
      ) : null}
      <div className={cn(mine ? 'max-w-[82%]' : 'max-w-[88%]')}>
        <div
          className={cn(
            'px-3.5 py-[11px] text-body leading-normal',
            mine
              ? 'rounded-[14px_4px_14px_14px] bg-ngahere text-white'
              : 'rounded-[4px_14px_14px_14px]',
            author === 'them' && 'border border-line bg-surface text-ink',
            author === 'smolit' && 'bg-hover text-ink',
          )}
        >
          {children}
          {timestamp ? (
            <div className={cn('mt-1 text-micro', mine ? 'text-white/60' : 'text-placeholder')}>
              {timestamp}
            </div>
          ) : null}
        </div>
        {attachment ? <div className="mt-2.5 flex flex-col gap-2">{attachment}</div> : null}
      </div>
    </div>
  );
}

export interface PromptChipProps {
  onClick: () => void;
  children: ReactNode;
}

/** The example prompts above Smolit's composer. */
export function PromptChip({ onClick, children }: PromptChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex-none cursor-pointer rounded-full border border-moana-line bg-moana-tint',
        'px-3 py-1.5 text-caption font-semibold whitespace-nowrap text-moana-ink',
        'transition-colors duration-fast ease-standard hover:bg-moana-tint-hover',
      )}
    >
      {children}
    </button>
  );
}
